import { supabase } from '../supabaseClient';
import { fetchWithBackendFallback } from './backendApi';

/**
 * Resolves the client's public IP address using public IP discovery services
 * with fast timeouts, falling back to the application backend.
 * @returns {Promise<string|null>} IP address string, or null if unreachable
 */
export const getUserIp = async () => {
  // 1. Check api64.ipify.org (dual IPv4/IPv6 support)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);
    const res = await fetch('https://api64.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data?.ip) return data.ip.trim();
    }
  } catch {
    // Continue to fallback
  }

  // 2. Fallback to api.ipify.org (IPv4)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2500);
    const res = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data?.ip) return data.ip.trim();
    }
  } catch {
    // Continue to backend fallback
  }

  // 3. Fallback to application backend endpoint if reachable
  try {
    const backendRes = await fetchWithBackendFallback('/auth/client-ip');
    if (backendRes.ok) {
      const data = await backendRes.json();
      if (data?.ip && data.ip !== 'unknown') return data.ip.trim();
    }
  } catch {
    // Ignore fallback failure
  }

  return null;
};

/**
 * Stores the user's IP address in tbl_users in the database.
 * Executes both the PostgreSQL record_user_ip RPC function and direct table update.
 * @param {string} email - User's email address
 * @param {string|null} [explicitIp] - Optional pre-resolved IP address
 * @returns {Promise<boolean>} True if recorded successfully
 */
export const recordUserIp = async (email, explicitIp = null) => {
  if (!email) return false;

  try {
    const ip = explicitIp || (await getUserIp());
    if (!ip) return false;

    // 1. Call PostgreSQL record_user_ip RPC
    try {
      const { data, error } = await supabase.rpc('record_user_ip', {
        p_email: email.trim(),
        p_ip: ip,
      });
      if (!error && data === true) {
        return true;
      }
    } catch (rpcErr) {
      console.warn('RPC record_user_ip error:', rpcErr);
    }

    // 2. Fallback: Direct tbl_users update
    try {
      const { error: updateErr } = await supabase
        .from('tbl_users')
        .update({ IPAddress: ip })
        .eq('email', email.trim());

      if (!updateErr) {
        return true;
      }
    } catch (updateErr) {
      console.warn('Direct tbl_users IP update error:', updateErr);
    }

    return false;
  } catch (err) {
    console.warn('recordUserIp error:', err);
    return false;
  }
};
